import { Box, TextLink, Typography } from "@drone-js/core";
import { useKeyDownHandler } from "apps/commons/hooks/useKeyDownHandler";
import { urlInputBinder } from "libs/analytics/binders/propertySelection/urlInput";
import { useAnalytics } from "libs/analytics/useAnalytics";
import { type FunctionComponent, useState } from "react";
import { useLocation } from "react-router";
import { useParams } from "router";
import { Footer } from "../Footer";
import { usePropertySelectionViewModelValue } from "../propertySelectionViewModelAtom";
import { InputURLImage } from "./InputURLImage";
import { LearnMoreModal } from "./LearnMoreModal";
import { NewURLInput } from "./NewURLInput";
import { ToastErrorNotification } from "./ToastErrorNotification";
import { URLInput } from "./UrlInput";
import { ValidationError } from "./constants";
import { useURLInputHandler } from "./useURLInputHandler";
import { useURLValidationHandler } from "./useURLValidationHandler";

export const UrlInputPage: FunctionComponent = () => {
    const {
        inputUrlCms: {
            learnMore,
            back,
            next,
            errorOngoingImportDetected,
            listingsStillBeingImported,
            viewCurrentImportListings,
            importListingPageTitle,
            importListingUrlInputText,
        },
        shouldUseNewerScrapingValidation,
    } = usePropertySelectionViewModelValue();

    const [isOpen, setIsOpen] = useState(false);
    const { validationError, setValidationError, loading, onNext, onBack } = useURLInputHandler();
    const { lang } = useParams("/:lang/list-my-property/get-started/automatic");

    const validationHandler = useURLValidationHandler();

    const { search } = useLocation();

    useKeyDownHandler(
        (e) => {
            if (e.key === "Enter") {
                if (shouldUseNewerScrapingValidation) validationHandler.onNext();
                else onNext();
            }
        },
        [onNext],
    );

    useAnalytics(urlInputBinder);

    const renderActionToastErrorNotification = () => {
        const isInProgressBatchExist = validationError === ValidationError.InProgressBatchExist;
        const isTechnicalIssue = validationError === ValidationError.TechnicalIssue;
        const isOpenToast = isInProgressBatchExist || isTechnicalIssue;
        const title = errorOngoingImportDetected;
        const Content = listingsStillBeingImported;
        const action = {
            label: viewCurrentImportListings,
            onClick: () => {
                window.location.assign(`/homes/${lang}/HostManage/#/listings/inprogress`);
            },
        };

        return isInProgressBatchExist ? (
            <ToastErrorNotification isOpen={isOpenToast} title={title} content={Content} actions={[action]} />
        ) : (
            <ToastErrorNotification isOpen={isOpenToast} />
        );
    };

    const renderLandingToastErrorNotification = () => {
        const errorLists = ["importSelection", "bcomError"];
        const error = new URLSearchParams(search).get("error");
        const isOpen = errorLists.includes(error);

        return <ToastErrorNotification isOpen={isOpen} />;
    };

    return (
        <Box py="32" display="flex" flexDirection="column">
            <Typography variant="heading-xl" mb="32" textColor="base.default">
                {importListingPageTitle}
            </Typography>
            <Typography variant="body-default" mb="4" me="4" textColor="base.default">
                {importListingUrlInputText}
            </Typography>

            {shouldUseNewerScrapingValidation ? (
                <NewURLInput {...validationHandler} onLearnMore={() => setIsOpen(true)} />
            ) : (
                <URLInput error={validationError} onChange={() => setValidationError(null)} />
            )}

            <InputURLImage />
            <Box pt="8" qM={{ pt: "16" }}>
                <TextLink
                    hierarchy="primary"
                    label={learnMore}
                    onClick={() => setIsOpen(true)}
                    data-element-name="learn-more-button"
                />
            </Box>
            {renderLandingToastErrorNotification()}
            {renderActionToastErrorNotification()}
            <Footer
                secondary={{
                    label: back,
                    dataElementName: "back",
                }}
                primary={{
                    label: next,
                    loading: shouldUseNewerScrapingValidation ? validationHandler.isLoading : loading,
                    dataElementName: "next",
                }}
                onSecondary={shouldUseNewerScrapingValidation ? validationHandler.onBack : onBack}
                onPrimary={shouldUseNewerScrapingValidation ? validationHandler.onNext : onNext}
            />
            <LearnMoreModal isOpen={isOpen} onClose={() => setIsOpen(false)} />
        </Box>
    );
};
